import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import store from 'redux/store';
import { setPendingChatInput, setPendingDsChatInput } from 'redux/mode/actions';
import { FileUploadCallbacks, UploadTracker } from 'utils';
import { Attachment } from 'dragonmouse-shared/interfaces';

type ChatType = 'ds' | 'scene';

interface UseFileUploadCallbacksOptions {
	type: ChatType;
	chatId: string | null;
}

export function useFileUploadCallbacks({
	type,
	chatId,
}: UseFileUploadCallbacksOptions): FileUploadCallbacks {
	const dispatch = useDispatch();

	return useMemo(() => {
		const isDs = type === 'ds';

		return {
			onLoadingStart: (uploadTracker: UploadTracker) => {
				if (!chatId) return;
				const currentState = store.getState();

				if (isDs) {
					const pendingDsChatInput = currentState.mode.pendingDsChatInputs[chatId];
					if (!pendingDsChatInput) return;
					dispatch(
						setPendingDsChatInput({
							dsChatId: chatId,
							pendingDsChatInput: {
								...pendingDsChatInput,
								loadingAttachments: [
									...(pendingDsChatInput.loadingAttachments || []),
									uploadTracker,
								],
							},
						})
					);
				} else {
					const pendingChatInput = currentState.mode.pendingChatInputs[chatId] as any;
					if (!pendingChatInput) return;
					dispatch(
						setPendingChatInput({
							chatId: chatId,
							pendingChatInput: {
								...pendingChatInput,
								loadingAttachments: [...(pendingChatInput.loadingAttachments || []), uploadTracker],
							},
						})
					);
				}
			},
			onLoadingEnd: (uniqueId: string) => {
				if (!chatId) return;
				const currentState = store.getState();

				if (isDs) {
					const pendingDsChatInput = currentState.mode.pendingDsChatInputs[chatId];
					if (!pendingDsChatInput) return;
					dispatch(
						setPendingDsChatInput({
							dsChatId: chatId,
							pendingDsChatInput: {
								...pendingDsChatInput,
								loadingAttachments: pendingDsChatInput.loadingAttachments?.filter(
									(a) => a.uniqueId !== uniqueId
								),
							},
						})
					);
				} else {
					const pendingChatInput = currentState.mode.pendingChatInputs[chatId] as any;
					if (!pendingChatInput) return;
					dispatch(
						setPendingChatInput({
							chatId: chatId,
							pendingChatInput: {
								...pendingChatInput,
								loadingAttachments: pendingChatInput.loadingAttachments?.filter(
									(a: UploadTracker) => a.uniqueId !== uniqueId
								),
							},
						})
					);
				}
			},
			onSuccess: (attachment: Attachment) => {
				if (!chatId) return;
				const currentState = store.getState();

				if (isDs) {
					const pendingDsChatInput = currentState.mode.pendingDsChatInputs[chatId];
					if (!pendingDsChatInput) return;
					if (
						pendingDsChatInput.attachments?.some(
							(a: Attachment) => a.storagePath === attachment.storagePath
						)
					) {
						return;
					}
					dispatch(
						setPendingDsChatInput({
							dsChatId: chatId,
							pendingDsChatInput: {
								...pendingDsChatInput,
								attachments: [...(pendingDsChatInput.attachments || []), attachment],
							},
						})
					);
				} else {
					const pendingChatInput = currentState.mode.pendingChatInputs[chatId] as any;
					if (!pendingChatInput) return;
					if (
						pendingChatInput.attachments?.some(
							(a: Attachment) => a.storagePath === attachment.storagePath
						)
					) {
						return;
					}
					dispatch(
						setPendingChatInput({
							chatId: chatId,
							pendingChatInput: {
								...pendingChatInput,
								attachments: [...(pendingChatInput.attachments || []), attachment],
							},
						})
					);
				}
			},
		};
	}, [dispatch, type, chatId]);
}
